import React, { useEffect, useState } from 'react';
import { MAX_LABEL_PARTS, reasonMessage, type FoundProduct } from './reducer';

const HINTS: Record<string, string> = {
  blurry: 'The photo was blurry. Hold the camera steady and try again.',
  glare: 'There was glare on the label. Move away from direct light.',
  not_ingredients: 'That photo does not show the ingredient list.',
  cut_off: 'Part of the list was cut off. Add another part.',
};

const REASON_NOTES: Record<string, string> = {
  none_found: 'We could not find this product. Photograph its ingredient list instead.',
};

/** Reason for a confirmed product whose ingredient list is not online. */
const FOUND_REASON = 'ingredients_unreadable';
const FOUND_NOTE = "Its ingredient list isn't online yet. Take 1–3 photos of the ingredients on the pack — no extra credits.";

export interface LabelCaptureProps {
  side: 'a' | 'b';
  parts: Blob[];
  attemptsLeft: number;
  hint?: string;
  reason?: string;
  /** Confirmed product, shown when only its ingredient list is missing. */
  found?: FoundProduct;
  reading: boolean;
  error?: string;
  onAddPart: (file: Blob) => void;
  onRemovePart: (index: number) => void;
  onRead: () => void;
  /** Set while confirming an unverified list: leave the step and keep that list. */
  onCancelVerify?: () => void;
}

function usePreviews(parts: Blob[]): (string | null)[] {
  const [urls, setUrls] = useState<(string | null)[]>([]);
  useEffect(() => {
    const created = parts.map((part) => (typeof URL.createObjectURL === 'function' ? URL.createObjectURL(part) : null));
    setUrls(created);
    return () => {
      for (const url of created) if (url) URL.revokeObjectURL(url);
    };
  }, [parts]);
  return urls;
}

/** Label step: 1–3 photos of the ingredient list, the attempts left and a hint. */
export default function LabelCapture({
  side, parts, attemptsLeft, hint, reason, found, reading, error, onAddPart, onRemovePart, onRead, onCancelVerify,
}: LabelCaptureProps) {
  const previews = usePreviews(parts);
  const isFound = !hint && reason === FOUND_REASON;
  const note = hint
    ? HINTS[hint] ?? reasonMessage('label_unreadable')
    : isFound
      ? FOUND_NOTE
      : reason ? REASON_NOTES[reason] ?? reasonMessage(reason) : null;
  const canAdd = parts.length < MAX_LABEL_PARTS && !reading;
  const foundName = found ? [found.brand, found.name].filter(Boolean).join(' ') : null;

  return (
    <div className="stack-3" data-testid="label-capture">
      {isFound ? (
        <div>
          <h3 className="t-small" style={{ margin: 0, fontWeight: 700, color: 'var(--ink)' }}>
            <span aria-hidden="true" style={{ color: 'var(--c-kivo-teal)', marginRight: 'var(--s-1)' }}>✓</span>
            Product found
          </h3>
          {foundName && <p className="t-small" style={{ margin: 0, color: 'var(--ink)' }}>{foundName}</p>}
        </div>
      ) : (
        <h3 className="t-small" style={{ margin: 0, fontWeight: 700, color: 'var(--ink)' }}>
          {onCancelVerify ? 'Confirm the ingredient list' : 'Photograph the ingredient list'}
        </h3>
      )}
      {onCancelVerify && !hint && (
        <p className="t-small t-mute" style={{ margin: 0 }}>
          Take 1–3 photos of the ingredients on the pack. We replace the unconfirmed list with what is printed — no extra credits.
        </p>
      )}
      {note && <p className="t-small t-mute" style={{ margin: 0 }}>{note}</p>}
      <p className="t-small t-mute" style={{ margin: 0 }}>
        {attemptsLeft === 1 ? '1 attempt left' : `${attemptsLeft} attempts left`}
      </p>

      {parts.length > 0 && (
        <ul className="flex flex-wrap gap-2" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {parts.map((part, i) => (
            <li key={`${i}-${part.size}`} className="card card--bordered" style={{ padding: 'var(--s-2)', display: 'flex', alignItems: 'center', gap: 'var(--s-2)' }}>
              {previews[i]
                ? <img src={previews[i] ?? undefined} alt={`Part ${i + 1}`} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 'var(--r-sm)' }} />
                : <span className="t-small">Part {i + 1}</span>}
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={reading}
                onClick={() => { onRemovePart(i); }}
                aria-label={`Remove part ${i + 1}`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      {canAdd && (
        <label className="btn btn--ghost btn--sm justify-center focus-within:outline focus-within:outline-2 focus-within:outline-[var(--c-kivo-teal)]" style={{ cursor: 'pointer' }}>
          {parts.length === 0 ? 'Add a photo of the list' : `Add another part (up to ${MAX_LABEL_PARTS})`}
          {/* No `capture`: on phones it forces the camera and blocks a photo the user already has. */}
          <input
            type="file"
            accept="image/*"
            className="sr-only"
            data-testid={`label-part-input-${side}`}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onAddPart(file);
              event.target.value = '';
            }}
          />
        </label>
      )}

      {error && <div className="notice notice--error" role="alert">{error}</div>}

      <button
        type="button"
        className="btn btn--primary btn--sm justify-center"
        disabled={parts.length === 0 || reading}
        onClick={onRead}
      >
        {reading ? 'Reading…' : 'Read the list'}
      </button>
      {onCancelVerify && (
        <button type="button" className="btn btn--ghost btn--sm justify-center" disabled={reading} onClick={onCancelVerify}>
          Keep the unconfirmed list
        </button>
      )}
    </div>
  );
}
